import { apiClient } from './client';

/** Seeded location kinds — kept in sync with the backend LOCATION_KINDS. */
export const LOCATION_KINDS = [
  'or',
  'sim_lab',
  'cadaver_lab',
  'classroom',
  'ultrasound',
  'other',
] as const;
export type LocationKind = (typeof LOCATION_KINDS)[number];

/** Human labels for the seeded kinds. */
export const LOCATION_KIND_LABELS: Record<LocationKind, string> = {
  or: 'Operating room',
  sim_lab: 'Simulation lab',
  cadaver_lab: 'Cadaver lab',
  classroom: 'Classroom',
  ultrasound: 'Ultrasound room',
  other: 'Other',
};

export interface Department {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Location {
  id: string;
  name: string;
  kind: string;
  department_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function listDepartments(tenantId: string): Promise<Department[]> {
  const { data } = await apiClient.get<Department[]>(
    `/api/v1/tenants/${tenantId}/departments`,
  );
  return data;
}

export async function createDepartment(
  tenantId: string,
  body: { name: string },
): Promise<Department> {
  const { data } = await apiClient.post<Department>(
    `/api/v1/tenants/${tenantId}/departments`,
    body,
  );
  return data;
}

export async function updateDepartment(
  tenantId: string,
  departmentId: string,
  body: { name?: string },
): Promise<Department> {
  const { data } = await apiClient.patch<Department>(
    `/api/v1/tenants/${tenantId}/departments/${departmentId}`,
    body,
  );
  return data;
}

export async function deleteDepartment(
  tenantId: string,
  departmentId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/departments/${departmentId}`,
  );
}

export async function listLocations(tenantId: string): Promise<Location[]> {
  const { data } = await apiClient.get<Location[]>(
    `/api/v1/tenants/${tenantId}/locations`,
  );
  return data;
}

export async function createLocation(
  tenantId: string,
  body: { name: string; kind: string; department_id?: string | null },
): Promise<Location> {
  const { data } = await apiClient.post<Location>(
    `/api/v1/tenants/${tenantId}/locations`,
    body,
  );
  return data;
}

export async function updateLocation(
  tenantId: string,
  locationId: string,
  body: { name?: string; kind?: string; department_id?: string | null },
): Promise<Location> {
  const { data } = await apiClient.patch<Location>(
    `/api/v1/tenants/${tenantId}/locations/${locationId}`,
    body,
  );
  return data;
}

export async function deleteLocation(
  tenantId: string,
  locationId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${tenantId}/locations/${locationId}`);
}
